import { Fragment, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useFetch from '../../../../hooks/use-fetch';

import { Dialog, Transition } from '@headlessui/react';
import Form, { Field } from '@atlaskit/form';
import TextField from '@atlaskit/textfield';
import Select from '@atlaskit/select';

import ErrorsBlock from '../../../../components/errors';

export default function FirmwareReleaseForm({ item, closeModal, isOpen, reloadTable, devicesModels }) {
  const { t } = useTranslation();
  const isNew = item === '';
  const attributes = item?.attributes || {};
  const targetTypeOptions = [
    { label: t('firmware_target_serial'), value: 'serial' },
    { label: t('firmware_target_model'), value: 'model' },
  ];
  const [targetType, setTargetType] = useState('serial');
  const [active, setActive] = useState(true);
  const [selectedModel, setSelectedModel] = useState(null);
  const [binary, setBinary] = useState(null);
  const [errors, setErrors] = useState([]);
  const [{ response: itemSaveResponse, error: itemSaveError }, doFetchItemSave] = useFetch('/administration/devices/firmware_releases', true);
  const [{ response: itemUpdateResponse, error: itemUpdateError }, doFetchItemUpdate] = useFetch(`/administration/devices/firmware_releases/${item.id}`, true);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setTargetType(attributes.target_type || 'serial');
    setActive(attributes.active !== false);
    setBinary(null);

    if (attributes.device_model_id) {
      setSelectedModel(devicesModels.find((model) => String(model.value) === String(attributes.device_model_id)) || null);
    } else {
      setSelectedModel(null);
    }
  }, [isOpen, item, devicesModels]);

  useEffect(() => {
    if (!itemSaveResponse) {
      return;
    }

    closeModal();
    reloadTable();
  }, [itemSaveResponse]);

  useEffect(() => {
    if (!itemUpdateResponse) {
      return;
    }

    closeModal();
    reloadTable();
  }, [itemUpdateResponse]);

  useEffect(() => {
    const errorPayload = itemSaveError || itemUpdateError;
    if (errorPayload) {
      setErrors(errorPayload.errors || []);
    }
  }, [itemSaveError, itemUpdateError]);

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setErrors([]);
      }, 300);
    }
  }, [isOpen]);

  const selectedTargetTypeOption = targetTypeOptions.find((option) => option.value === targetType);

  const onSaveClick = (data) => {
    const formData = new FormData();
    formData.set('data[version]', data.version || '');
    formData.set('data[target_type]', targetType);
    formData.set('data[active]', active ? '1' : '0');
    formData.set('data[notes]', data.notes || '');

    if (targetType === 'serial') {
      formData.set('data[serial_targets]', data.serial_targets || '');
    } else {
      formData.set('data[device_model_id]', selectedModel?.value || '');
    }

    if (binary) {
      formData.set('data[binary]', binary);
    }

    if (isNew) {
      doFetchItemSave({
        method: 'POST',
        data: formData,
      });
    } else {
      doFetchItemUpdate({
        method: 'PUT',
        data: formData,
      });
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={closeModal}
      >
        <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
        <div className="min-h-screen px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block w-full max-w-2xl p-6 mt-24 my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded">
              <Dialog.Title
                as="h3"
                className="text-lg font-medium leading-6 text-gray-900"
              >
                {isNew ? t('adding_firmware_release') : t('editing_firmware_release')}
              </Dialog.Title>
              {errors.length > 0 ? <ErrorsBlock errors={errors} /> : ''}
              <Form key={item?.id || 'new'} onSubmit={(data) => onSaveClick(data)}>
                {({ formProps }) => (
                  <form {...formProps} className="text-sm">
                    <Field name="version" defaultValue={attributes.version || ''} label={t('firmware_version')} isRequired>
                      {({ fieldProps }) => <TextField {...fieldProps} />}
                    </Field>

                    <Field name="target_type" label={t('firmware_target_type')} isRequired>
                      {({ fieldProps }) => (
                        <Select
                          {...fieldProps}
                          options={targetTypeOptions}
                          value={selectedTargetTypeOption}
                          onChange={(option) => setTargetType(option.value)}
                        />
                      )}
                    </Field>

                    {targetType === 'serial' ? (
                      <Field
                        name="serial_targets"
                        defaultValue={(attributes.serial_targets || []).join('\n')}
                        label={t('firmware_serial_targets')}
                        isRequired
                      >
                        {({ fieldProps }) => (
                          <textarea
                            {...fieldProps}
                            rows={5}
                            className="block w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                          />
                        )}
                      </Field>
                    ) : (
                      <Field name="device_model_id" label={t('device_model')} isRequired>
                        {({ fieldProps }) => (
                          <Select
                            {...fieldProps}
                            options={devicesModels}
                            value={selectedModel}
                            onChange={(option) => setSelectedModel(option)}
                            isClearable
                          />
                        )}
                      </Field>
                    )}

                    <Field name="notes" defaultValue={attributes.notes || ''} label={t('notes')}>
                      {({ fieldProps }) => (
                        <textarea
                          {...fieldProps}
                          rows={3}
                          className="block w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                        />
                      )}
                    </Field>

                    <label className="block mt-4 text-sm font-medium text-gray-700">
                      {t('firmware_file')}
                      <input
                        aria-label={t('firmware_file')}
                        type="file"
                        accept=".bin"
                        className="block w-full mt-2 text-sm"
                        onChange={(event) => setBinary(event.target.files[0] || null)}
                        required={isNew}
                      />
                    </label>

                    <label className="flex items-center mt-4 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={(event) => setActive(event.target.checked)}
                        className="mr-2"
                      />
                      {t('active')}
                    </label>

                    <div className="flex justify-between mt-8">
                      <button
                        type="submit"
                        className="inline-flex justify-center px-4 py-2 text-sm font-medium text-blue-900 bg-blue-100 border border-transparent rounded hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                      >
                        {t('save')}
                      </button>
                      <button
                        onClick={() => closeModal()}
                        type="button"
                        className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-900 bg-gray-100 border border-transparent rounded hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                      >
                        {t('cancel')}
                      </button>
                    </div>
                  </form>
                )}
              </Form>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
